/**
 * Evaluate workflow dynamic form "conditional behavior" for a single field.
 *
 * Schema on field.conditionalBehavior:
 * - enabled: boolean
 * - sourceFieldId: id of controlling field
 * - operator: 'equals' | 'not_equals'
 * - value: string compared to controlling field (case-insensitive trim)
 * - hideWhenMatch: when enabled, field is hidden when condition matches (e.g. hide reason when Deferred = yes)
 * - hideUnlessMatch: when enabled, field is visible only when condition matches (inverse of hideWhenMatch)
 * - requireWhenMatch: when enabled, field is required when condition matches (in addition to field.required)
 */

const DEFAULT_BEHAVIOR = {
  enabled: false,
  sourceFieldId: "",
  operator: "equals",
  value: "",
  hideWhenMatch: false,
  hideUnlessMatch: false,
  requireWhenMatch: false,
}

export const CONDITIONAL_VISIBILITY_MODES = {
  ALWAYS: "always",
  HIDE_WHEN: "hide_when",
  SHOW_WHEN: "show_when",
}

export function normalizeConditionalBehavior(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_BEHAVIOR }
  }
  const sourceFieldId = raw.sourceFieldId ?? raw.source_field_id ?? ""
  const hideUnlessRaw = Boolean(
    raw.hideUnlessMatch ?? raw.hide_unless_match,
  )
  const hideWhenRaw = raw.hideWhenMatch ?? raw.hide_when_match
  const hideWhenExplicit =
    Object.prototype.hasOwnProperty.call(raw, "hideWhenMatch") ||
    Object.prototype.hasOwnProperty.call(raw, "hide_when_match")

  let hideWhenMatch = Boolean(hideWhenRaw)
  let hideUnlessMatch = hideUnlessRaw

  // Keep "show only when" semantics stable when only hideUnlessMatch is present.
  // Some payloads omit false booleans, so missing hideWhenMatch should not invert behavior.
  if (hideUnlessRaw && !hideWhenExplicit) {
    hideWhenMatch = false
    hideUnlessMatch = true
  }

  return {
    enabled: Boolean(raw.enabled),
    sourceFieldId: sourceFieldId != null ? String(sourceFieldId) : "",
    operator: raw.operator === "not_equals" ? "not_equals" : "equals",
    value: raw.value != null ? String(raw.value) : "",
    hideWhenMatch,
    hideUnlessMatch,
    requireWhenMatch: Boolean(raw.requireWhenMatch ?? raw.require_when_match),
  }
}

export function conditionalVisibilityMode(raw) {
  const behavior = normalizeConditionalBehavior(raw)
  if (behavior.hideUnlessMatch) {
    return CONDITIONAL_VISIBILITY_MODES.SHOW_WHEN
  }
  if (behavior.hideWhenMatch) {
    return CONDITIONAL_VISIBILITY_MODES.HIDE_WHEN
  }

  return CONDITIONAL_VISIBILITY_MODES.ALWAYS
}

export function conditionalBehaviorWithVisibilityMode(raw, mode) {
  const base = normalizeConditionalBehavior(raw)

  if (mode === CONDITIONAL_VISIBILITY_MODES.HIDE_WHEN) {
    return normalizeConditionalBehavior({
      ...base,
      hideWhenMatch: true,
      hideUnlessMatch: false,
    })
  }

  if (mode === CONDITIONAL_VISIBILITY_MODES.SHOW_WHEN) {
    return normalizeConditionalBehavior({
      ...base,
      hideWhenMatch: false,
      hideUnlessMatch: true,
    })
  }

  return normalizeConditionalBehavior({
    ...base,
    hideWhenMatch: false,
    hideUnlessMatch: false,
  })
}

function sourceValueAsString(formData, sourceFieldId) {
  if (!sourceFieldId) return ""
  const v = formData[sourceFieldId]
  if (v === true) return "true"
  if (v === false) return "false"
  if (v === undefined || v === null) return ""
  return String(v).trim()
}

function comparableTokens(raw) {
  const base = (raw ?? "").toString().trim().toLowerCase()
  if (!base) return []

  const tokens = new Set([base])

  // Support legacy/custom values saved as "Label — value" or "Label - value".
  const splitters = ["—", "-"]
  for (const splitter of splitters) {
    if (base.includes(splitter)) {
      base
        .split(splitter)
        .map((p) => p.trim())
        .filter(Boolean)
        .forEach((p) => tokens.add(p))
    }
  }

  return Array.from(tokens)
}

function isSelectableInputField(f) {
  const t = f?.type
  return t === "select" || t === "radio" || t === "checkbox"
}

function findNearestSelectableBefore(dependentFieldId, allFields) {
  const depIdx = allFields.findIndex(
    (f) => String(f.id) === String(dependentFieldId),
  )
  if (depIdx <= 0) return null
  for (let i = depIdx - 1; i >= 0; i--) {
    const f = allFields[i]
    if (isSelectableInputField(f)) return f
  }
  return null
}

/**
 * Read controlling field value. If sourceFieldId is missing from the current form (stale),
 * use the nearest select/radio/checkbox above the dependent field.
 */
function resolveSourceRaw(formData, b, dependentFieldId, allFields) {
  const sid = String(b.sourceFieldId)
  let raw = sourceValueAsString(formData, sid)
  if (raw !== "") return raw

  const direct = allFields?.find((f) => String(f.id) === sid)
  if (direct) {
    return ""
  }

  const fallback = findNearestSelectableBefore(dependentFieldId, allFields)
  if (!fallback?.id) return ""
  return sourceValueAsString(formData, fallback.id)
}

function fieldHasSelectOptions(field) {
  return (
    field != null &&
    (field.type === "select" || field.type === "radio") &&
    Array.isArray(field.options) &&
    field.options.length > 0
  )
}

/**
 * Field used to infer a lost "value to match" from option labels (select/radio only).
 */
function controllerFieldForOptionInference(b, dependentFieldId, allFields) {
  if (!allFields?.length || !b.sourceFieldId) return null
  const sid = String(b.sourceFieldId)
  const direct = allFields.find((f) => String(f.id) === sid)
  if (fieldHasSelectOptions(direct)) return direct

  const fb = findNearestSelectableBefore(dependentFieldId, allFields)
  return fieldHasSelectOptions(fb) ? fb : null
}

/**
 * When stored match value was lost (snapshot/API), infer from controlling select options
 * (e.g. label "Deferred" → option value).
 */
function inferredMatchValue(b, allFields, dependentFieldId) {
  const ctrl = controllerFieldForOptionInference(b, dependentFieldId, allFields)
  if (!ctrl?.options || !Array.isArray(ctrl.options)) return ""
  const opt = ctrl.options.find((o) =>
    /\bdefer/i.test(String(o.label ?? "")),
  )
  if (opt == null) return ""
  return opt.value != null ? String(opt.value) : ""
}

/**
 * @param {Record<string, unknown>} formData
 * @param {Array<{ id?: string, type?: string, label?: string, options?: unknown[] }>} [allFields] all form fields (order preserved)
 */
export function conditionalBehaviorMatches(field, formData, allFields) {
  const b = normalizeConditionalBehavior(field?.conditionalBehavior)
  if (!b.enabled || !b.sourceFieldId) return true

  const sourceRaw = resolveSourceRaw(formData, b, field?.id, allFields)
  const leftTokens = comparableTokens(sourceRaw)

  const valueStr =
    String(b.value ?? "").trim() ||
    inferredMatchValue(b, allFields, field?.id) ||
    ""

  const rightTokens = comparableTokens(valueStr)

  if (rightTokens.length === 0 || leftTokens.length === 0) {
    return false
  }
  const rightSet = new Set(rightTokens)
  const equal = leftTokens.some((t) => rightSet.has(t))
  return b.operator === "not_equals" ? !equal : equal
}

/** @param {Record<string, unknown>} formData */
export function isFormFieldVisible(field, formData, allFields) {
  const b = normalizeConditionalBehavior(field?.conditionalBehavior)
  if (!b.enabled) return true

  const match = conditionalBehaviorMatches(field, formData, allFields)

  // If both flags are set (legacy/bad saved state), prefer "show only when"
  // because it is the more restrictive and explicit behavior.
  if (b.hideUnlessMatch) return match
  if (b.hideWhenMatch) return !match
  return true
}

/** @param {Record<string, unknown>} formData */
export function isFormFieldRequired(field, formData, allFields) {
  if (!isFormFieldVisible(field, formData, allFields)) return false
  const b = normalizeConditionalBehavior(field?.conditionalBehavior)
  const match = b.enabled
    ? conditionalBehaviorMatches(field, formData, allFields)
    : false
  const whenMatch = b.enabled && b.requireWhenMatch && match
  return Boolean(field.required) || whenMatch
}

/**
 * Binds `formData` and the full field list so UI code does not repeat the same arguments.
 * @param {Record<string, unknown>} formData
 * @param {Array} [allFields] fields in display order
 */
export function formConditionEvaluators(formData, allFields) {
  return {
    isFieldVisible: (field) => isFormFieldVisible(field, formData, allFields),
    isFieldRequired: (field) => isFormFieldRequired(field, formData, allFields),
  }
}
